import { Card, Grid, Typography, Button, FormControlLabel, Checkbox, TextField, Box, Alert, AlertTitle } from '@mui/material';
import React, { useEffect, useState } from 'react'
import { Field, FormikProvider, useFormik } from 'formik';

import * as Yup from "yup";
import api from '../../../redux/features/auth/axiosAuthHelper';
import dayjs from 'dayjs';
import { TextEditField, TextEditReadOnlyField, FormDatePicker, TestTypeSelectField } from '../fields/fields';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import colorConfigs from '../../../configs/colorConfigs';
import { DeviceSessionStorageKey, DeviceTableParameterStorageService } from '../../../services/sessionStorage';
import RareButton from '../buttons/RareButton';
import { Device } from '../types';

interface DeviceDetailProps {
    device_id?: any | null;
    handleRefreshButton: () => void;
    handleBackButton: () => void;
}


const DeviceDetailCard: React.FC<DeviceDetailProps> = ({ device_id, handleRefreshButton, handleBackButton }) => {
    const authData = useSelector((state: RootState) => state.auth);
    const [errors, setErrors] = useState<string | null>(null);
    const [initialValues, setInitialValues] = useState<any>({})
    const [updatedValues, setUpdatedValues] = useState<any>({})
    const [hasMadeChanges, setHasMadeChanges] = useState<boolean>(false)
    const [device, setDevice] = useState<Device>();

    const create = !device_id;

    // Fetch device data and point_model JSON data
    useEffect(() => {
        if (device_id) {
            const fetchDevice = async () => {
                try {
                    const url = `devices/${device_id}`;

                    const response = await api.get(url, {
                        headers: {
                            'Authorization': `Bearer ${authData.access}`
                        }
                    });
                    setDevice(response.data as Device);
                } catch (error) {
                    console.error('Error fetching device', error);
                    setErrors(`No Device found with ID ${device_id}`)
                    setTimeout(() => {
                        handleBackButton()
                    }, 4000);
                }
            };
            fetchDevice();
        }
    }, [device_id]);

    const validationSchema = () => {
        return Yup.object().shape({
        });
    }

    const formik = useFormik({
        enableReinitialize: !create,
        validateOnChange: false,
        validateOnBlur: false,
        initialValues:
            (!create && device) ? {
                id: device.id,
                name: device.name,
                model: device.model,
                ultra_wide: device.ultra_wide || false,
                h_factor: device.h_factor || 0,
                is_active: device.is_active || true
            }
                : {
                    id: "",
                    name: "",
                    model: "",
                    ultra_wide: false,
                    h_factor: 0,
                    is_active: true
                },
        validationSchema,
        onSubmit: async (values) => {

            const data = authData.isPortalUser ? {
                name: values.name,
                model: values.model,
                ultra_wide: values.ultra_wide,
                h_factor: values.h_factor,
                is_active: values.is_active,
            } : {

            }

            const url = device ? `devices/${device.id}/` : `devices/`
            const method = device ? 'patch' : 'post'

            try {
                console.log("data", data)
                await api({
                    method,
                    url,
                    data,
                    headers: {
                        'Authorization': `Bearer ${authData.access}`,
                    },
                });
                setErrors(null);
                console.log("Device successfully updated")
                handleRefreshButton() // reset states and close popup
                const count = DeviceTableParameterStorageService.get(DeviceSessionStorageKey.count)
                DeviceTableParameterStorageService.set(DeviceSessionStorageKey.count, count + 1) // Updated count to not trigger refresh data button
            } catch (error: any) {
                if (error?.response?.status === 400) {
                    const firstErrorItem = Object.keys(error.response.data)[0];
                    setErrors(`${firstErrorItem}: ${error.response.data[firstErrorItem]}`);
                } else if (error?.response?.status === 403) {
                    const firstErrorItem = Object.keys(error.response.data)[0];
                    setErrors(`${error.response.data[firstErrorItem]}`);
                } else {
                    const errorText = `Unknown error updating lot`;
                    setErrors(errorText);
                    console.error(errorText, error);
                }
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        },
    });


    // Effect to set initial form values from device to check for form changes
    useEffect(() => {
        setTimeout(() => {
            setInitialValues(formik.initialValues);
            setUpdatedValues(formik.initialValues);
        }, 500);
        setHasMadeChanges(false);
    }, [device]);

    // Update the updatedValues state when formik values change
    useEffect(() => {
        setUpdatedValues(formik.values);
    }, [formik.values]);

    // Function to check if form has changed so the save button becomes active on edit
    const hasFormChanged = (flatModel: Record<string, any>, formValues: Record<string, any>): boolean => {
        for (const key in formValues) {
            if (key === "expiry_date") {
                const formDate = dayjs(formValues[key]).format('YYYY-MM-DD');
                const initialDate = create ? dayjs(flatModel[key]).format('YYYY-MM-DD') : dayjs(formValues[key]).format('YYYY-MM-DD');
                if (formDate !== initialDate) {
                    console.log("Changed Date", formDate, initialDate)
                    return true;
                }
            } else {
                if (formValues[key] != flatModel[key]) {
                    console.log("Changed Data", formValues[key], flatModel[key])
                    return true;
                }
            }
        }
        return false;
    };

    // Use Effect to check if any changes ahve been made to the form on Edit
    useEffect(() => {
        if (!create && device) {
            if (Object.keys(initialValues).length > 0 && Object.keys(updatedValues).length > 0) {
                const formChanged = hasFormChanged(initialValues, updatedValues);
                setHasMadeChanges(formChanged);
            }
        }
    }, [updatedValues, initialValues, device]);

    // Use Effect set save button active on load if Create and if changes are made on Edit
    useEffect(() => {
        if (create) {
            setHasMadeChanges(true);
        } else {
            setHasMadeChanges(false);
        }
    }, [create]);

    return (
        <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit} style={{ overflow: 'auto' }}>
                <Box data-testid="Box-arsr" sx={{ height: "100%", p: 2, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                    {errors && (
                        <Alert data-testid="Alert-hh6d" severity="error" style={{ width: "59.5%" }}>
                            <AlertTitle data-testid="AlertTitle-9chd">Error</AlertTitle>
                            {errors}
                        </Alert>
                    )}
                    <div data-testid="div-8fpf" style={{ width: "100%", height: "100%", margin: "auto", padding: "1em", display: "flex", alignItems: "center" }}>
                        <Grid container spacing={5} alignItems="top" justifyContent="center" style={{ minWidth: "100%", padding: 5 }}>
                            <Grid item xs={12} md={5} style={{ display: "flex", flexDirection: "column", }}>
                                <Card data-testid="Card-x66h" sx={{ pb: 1, boxShadow: 10, height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <Box data-testid="Box-bv5w" sx={{ display: 'flex' }}>
                                        <Box data-testid="Box-6w4q" sx={{ height: '100%', background: colorConfigs.tables.headBg, display: 'flex', alignItems: 'center', borderBottomRightRadius: 3 }}>
                                            <Typography data-testid="Typography-ybxr" sx={{ px: 2, py: 0.5, color: '#fff', fontWeight: 'bold', fontSize: '20px' }}>Phone Configuration Details</Typography>
                                        </Box>
                                    </Box>
                                    <Box data-testid="Box-na55" sx={{ p: 2, pt: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
                                        {!create && <Field data-testid="Field-txd4" name="id" disableds type="text" className="form-control" label="ID" component={TextEditReadOnlyField} />}
                                        <Field data-testid="Field-s9rv" name="name" type="text" className="form-control" label="Device Name" component={TextEditField} />
                                        <Field data-testid="Field-f604" name="model" type="text" className="form-control" label="Device Model" component={TextEditField} />
                                        <Field data-testid="Field-wuqp" name="h_factor" type="number" className="form-control" label="H Factor" component={TextEditField} />

                                        <Box data-testid="Box-ub3g" sx={{ display: 'flex', justifyContent: 'space-between', gap: 3 }}>

                                            <FormControlLabel
                                                sx={{}}
                                                control={
                                                    <Checkbox
                                                        checked={formik.values.is_active}
                                                        name="is_active"
                                                        sx={{
                                                            '&.Mui-checked': {
                                                                color: colorConfigs.buttons.active,
                                                            },
                                                        }}
                                                        disabled={!authData.isPortalUser}
                                                        onChange={formik.handleChange}
                                                        className="form-control"
                                                    />
                                                }
                                                label={
                                                    <Box data-testid="Box-cwme" display="flex" alignItems="center">
                                                        Active?
                                                        <Typography data-testid="Typography-v3fq"
                                                            fontSize="smaller"
                                                            sx={{
                                                                paddingX: 0.7,
                                                                border: formik.values.is_active
                                                                    ? `2px solid ${colorConfigs.buttons.active}`
                                                                    : `1px solid ${colorConfigs.buttons.inactive}`,
                                                                borderRadius: 2,
                                                                color: formik.values.is_active
                                                                    ? colorConfigs.buttons.active
                                                                    : colorConfigs.buttons.inactive,
                                                                marginLeft: 1,
                                                            }}
                                                        >
                                                            {formik.values.is_active ? 'Active' : 'Inactive'}
                                                        </Typography>
                                                    </Box>
                                                }
                                            />

                                            <FormControlLabel
                                                sx={{ mt: 0.5, mb: 0 }}
                                                control={
                                                    <Checkbox
                                                        checked={formik.values.ultra_wide}
                                                        name="ultra_wide"
                                                        sx={{
                                                            '&.Mui-checked': {
                                                                color: colorConfigs.buttons.active,
                                                            },
                                                        }}
                                                        disabled={!authData.isPortalUser}
                                                        onChange={formik.handleChange}
                                                        className="form-control"
                                                    />
                                                }
                                                label={
                                                    <Box data-testid="Box-g43j" display="flex" alignItems="center">
                                                        Ultra Wide?
                                                        <Typography data-testid="Typography-0yvv"
                                                            fontSize="smaller"
                                                            sx={{
                                                                paddingX: 0.7,
                                                                border: formik.values.ultra_wide
                                                                    ? `2px solid ${colorConfigs.buttons.active}`
                                                                    : `1px solid ${colorConfigs.buttons.inactive}`,
                                                                borderRadius: 2,
                                                                color: formik.values.ultra_wide
                                                                    ? colorConfigs.buttons.active
                                                                    : colorConfigs.buttons.inactive,
                                                                marginLeft: 1,
                                                            }}
                                                        >
                                                            {formik.values.ultra_wide ? 'Yes' : 'No'}
                                                        </Typography>
                                                    </Box>
                                                }
                                            />

                                        </Box>
                                    </Box>
                                </Card>
                            </Grid>

                        </Grid>
                    </div>
                    <div data-testid="div-u2ng" className="form-group" style={{ width: "100%", display: 'flex', alignItems: 'center', justifyContent: "space-between" }}>
                        <RareButton data-testid="RareButton-7fvv"
                            onClick={() => handleBackButton()}
                            variant="outlined"
                            sx={{
                                position: "relative",
                                m: 1,
                            }}
                        >
                            Back
                        </RareButton>
                        <RareButton data-testid="RareButton-1seu"
                            type="submit"
                            variant="success"
                            // disabled={!hasMadeChanges}
                            sx={{
                                position: "relative",
                                m: 1,
                            }}
                        >
                            Save Changes
                        </RareButton>
                    </div>
                </Box>
            </form>
        </FormikProvider>
    )
}

export default DeviceDetailCard;